import { useEffect, useRef, useState } from "react";

/**
 * CursorCompanion — a desktop-only rose-gold ring that follows the cursor and
 * enlarges + blurs the background when hovering interactive elements.
 *
 * Implementation notes:
 *   - Pure DOM + requestAnimationFrame; no Framer Motion overhead per-frame.
 *   - Uses `mix-blend-mode: difference` so the ring stays legible on light or
 *     dark surfaces without re-tinting.
 *   - Detects interactive elements by tag (a/button/input/textarea/[role=button]).
 *   - Hidden entirely on touch devices and when prefers-reduced-motion is set.
 *
 * Also exports a CSS custom-property pair (`--mx`, `--my`) on the body so
 * liquid-glass cards can draw a radial gradient that follows the cursor.
 */

const INTERACTIVE_SELECTOR =
  "a, button, input, textarea, select, label, [role='button'], [data-interactive='true']";

function detectSupport() {
  if (typeof window === "undefined") return false;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const hasFinePointer = window.matchMedia("(pointer: fine)").matches;
  return !reducedMotion && hasFinePointer;
}

function ringStyle(hovering) {
  return {
    width: hovering ? 64 : 18,
    height: hovering ? 64 : 18,
    borderRadius: "999px",
    border: `1px solid var(--noor-rosegold, #C9A47A)`,
    background: hovering ? "rgba(201,164,122,0.10)" : "transparent",
    backdropFilter: hovering ? "blur(8px) saturate(160%)" : "none",
    WebkitBackdropFilter: hovering ? "blur(8px) saturate(160%)" : "none",
    mixBlendMode: "difference",
    transition: "width .35s cubic-bezier(.22,1,.36,1), height .35s cubic-bezier(.22,1,.36,1), background .25s ease, backdrop-filter .25s ease",
  };
}

// Tracks the cursor position into targetRef and exposes --mx/--my on <body>.
function useCursorTracking(active, targetRef) {
  useEffect(() => {
    if (!active) return undefined;
    const onMove = (e) => {
      targetRef.current.x = e.clientX;
      targetRef.current.y = e.clientY;
      document.body.style.setProperty("--mx", `${e.clientX}px`);
      document.body.style.setProperty("--my", `${e.clientY}px`);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [active, targetRef]);
}

// Flips `hovering` whenever the pointer enters/leaves an interactive surface.
function useInteractiveHover(active, setHovering) {
  useEffect(() => {
    if (!active) return undefined;
    const onOver = (e) => {
      if (e.target?.closest?.(INTERACTIVE_SELECTOR)) setHovering(true);
    };
    const onOut = (e) => {
      if (e.target?.closest?.(INTERACTIVE_SELECTOR)) setHovering(false);
    };
    window.addEventListener("mouseover", onOver, { passive: true });
    window.addEventListener("mouseout", onOut, { passive: true });
    return () => {
      window.removeEventListener("mouseover", onOver);
      window.removeEventListener("mouseout", onOut);
    };
  }, [active, setHovering]);
}

// Drives the rAF loop that interpolates the ring toward the cursor target.
function useDampedFollow(active, ringRef, targetRef, currentRef) {
  useEffect(() => {
    if (!active) return undefined;
    let raf = 0;
    const tick = () => {
      const cur = currentRef.current;
      const tgt = targetRef.current;
      cur.x += (tgt.x - cur.x) * 0.22;
      cur.y += (tgt.y - cur.y) * 0.22;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${cur.x}px, ${cur.y}px, 0) translate(-50%, -50%)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, ringRef, targetRef, currentRef]);
}

export default function CursorCompanion() {
  const ringRef = useRef(null);
  const targetRef = useRef({ x: -50, y: -50 });
  const currentRef = useRef({ x: -50, y: -50 });
  // Lazy initial state — set once, never re-evaluated in an effect.
  const [supported] = useState(detectSupport);
  const [hovering, setHovering] = useState(false);

  useCursorTracking(supported, targetRef);
  useInteractiveHover(supported, setHovering);
  useDampedFollow(supported, ringRef, targetRef, currentRef);

  if (!supported) return null;

  return (
    <div
      ref={ringRef}
      data-testid="cursor-companion"
      aria-hidden
      className="pointer-events-none fixed top-0 left-0 z-[200]"
      style={ringStyle(hovering)}
    />
  );
}
